
import React, { useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, Image } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { uploadMediaFile } from "../utils/mediaUpload";

interface ImageUploaderProps {
  imageUrl: string;
  onImageChange: (url: string) => void;
  disabled?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ 
  imageUrl, 
  onImageChange,
  disabled = false
}) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    
    const { publicUrl, error } = await uploadMediaFile(file);
    
    if (error) {
      toast({
        title: language === "en" ? "Upload failed" : "فشل التحميل",
        description: error.message,
        variant: "destructive"
      });
    } else if (publicUrl) {
      onImageChange(publicUrl);
      toast({
        title: language === "en" ? "Upload successful" : "تم التحميل بنجاح",
        description: language === "en" ? "Image has been uploaded" : "تم تحميل الصورة"
      });
    }
    
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex space-x-2">
        <Input 
          placeholder={language === "en" ? "Enter image URL" : "أدخل رابط الصورة"} 
          value={imageUrl} 
          onChange={(e) => onImageChange(e.target.value)}
          disabled={disabled}
        />
        <Button 
          type="button" 
          variant="outline" 
          className="flex-shrink-0" 
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={disabled}
        />
        <Button 
          type="button" 
          variant="outline" 
          className="flex-shrink-0" 
          onClick={() => imageUrl && window.open(imageUrl, '_blank')}
          disabled={disabled || !imageUrl}
        >
          <Image className="h-4 w-4" />
        </Button>
      </div>
      
      {imageUrl && (
        <div className="mt-2 relative w-full h-32 rounded-md overflow-hidden border border-input">
          <img 
            src={imageUrl} 
            alt="Preview" 
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/placeholder.svg";
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
